import { useState, type ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import { Layout } from 'antd';
import { DeploymentUnitOutlined } from '@ant-design/icons';
import { SideNav } from './SideNav';
import { TopBar } from './TopBar';
import { PageTitleContext } from './pageTitle';
import { PageErrorBoundary } from './PageErrorBoundary';
import { TourProvider } from '@/edu/TourProvider';
import { HelpPanelHost } from '@/help/HelpPanel';
import { CommandPaletteHost } from '@/palette/CommandPaletteHost';
import { ModuleOffGuard } from '@/modules/ModuleOffGuard';

const { Sider, Header, Content } = Layout;

function Brand({ collapsed }: { collapsed: boolean }) {
  return (
    <div
      style={{
        height: 52,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        paddingInline: collapsed ? 0 : 16,
        justifyContent: collapsed ? 'center' : 'flex-start',
        borderBottom: '1px solid var(--ol-border)',
      }}
    >
      <DeploymentUnitOutlined style={{ fontSize: 20, color: 'var(--ol-primary)' }} />
      {!collapsed && (
        <span style={{ fontWeight: 700, letterSpacing: 0.2, fontSize: 15 }}>OneLattice</span>
      )}
    </div>
  );
}

/** The persistent application shell: collapsible left nav, top bar, routed content. */
export function AppShell() {
  const [collapsed, setCollapsed] = useState(false);
  const [title, setTitle] = useState<ReactNode>(null);

  return (
    <TourProvider>
      <Layout style={{ height: '100vh' }}>
        <Sider
          theme="dark"
          collapsible
          collapsed={collapsed}
          trigger={null}
          width={220}
          collapsedWidth={64}
          style={{ borderInlineEnd: '1px solid var(--ol-border)' }}
        >
          <Brand collapsed={collapsed} />
          <SideNav collapsed={collapsed} />
        </Sider>
        <Layout>
          <Header
            style={{
              padding: 0,
              borderBottom: '1px solid var(--ol-border)',
              lineHeight: 'normal',
            }}
          >
            <TopBar
              collapsed={collapsed}
              onToggleCollapse={() => setCollapsed((c) => !c)}
              title={title}
            />
          </Header>
          <Content
            style={{
              height: '100%',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <PageTitleContext.Provider value={{ setTitle }}>
              <PageErrorBoundary>
                <ModuleOffGuard>
                  <Outlet />
                </ModuleOffGuard>
              </PageErrorBoundary>
            </PageTitleContext.Provider>
          </Content>
        </Layout>
      </Layout>
      {/* Global "About this page" panel (OPSFL-HELP-1): mounted once, keyed off the route,
          opened by the `?` hotkey or HelpMenu — zero per-page edits. */}
      <HelpPanelHost />
      {/* Global command palette (OPSFL-POLISH-1): mounted once, Cmd/Ctrl+K from every surface —
          including pages owned by other lanes, which need no edit to gain it. */}
      <CommandPaletteHost />
    </TourProvider>
  );
}
